import { useState, useEffect, useCallback } from 'react'

import { useForm } from 'react-hook-form'

import axios from 'axios'

import useSentry from 'Pods/Shared/Hooks/useSentry'
import I18n from 'Pods/Shared/I18n'

export function useLogin({ captchaId, locale, onSuccess, defaultEmail = '' }) {
  // Memorize translate function for consistency
  const translate = useCallback(
    (key) => I18n.t(key, { ns: 'rails', lng: locale }),
    [locale]
  )

  const { captureException } = useSentry()

  const [showPassword, setShowPassword] = useState(false)
  const [twoFASecondState, setTwoFASecondState] = useState(false)
  const [twoFaCode, setTwoFaCode] = useState('')
  const [loginCaptchaEnabled, setLoginCaptchaEnabled] = useState(false)
  const [remember, setRemember] = useState(true)

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors, touchedFields },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: defaultEmail,
      email_confirmation: '',
      password: '',
    },
  })

  useEffect(() => {
    setLoginCaptchaEnabled(window.gon?.features?.lcaptcha || window.pageConfig?.features?.lcaptcha)
  }, [])

  function captchaIsValid() {
    const captchaHasRendered = window.renderedcb
    const captchaJsLoaded = window.jsloadedcb
    const verifiedCaptcha = window.verifiedcb
    const captchaIsFillable = captchaHasRendered && captchaJsLoaded

    return captchaIsFillable ? verifiedCaptcha : true
  }

  function onSubmitSuccess(response) {
    const is2FaEnabled = response.twoFaEnabled || response.data.twoFaEnabled

    if (!response.data.success && !is2FaEnabled) {
      return onSubmitFailure({ response })
    }

    if (is2FaEnabled) {
      setTwoFASecondState(true)
    } else if (typeof response.data === 'string') {
      // Security tool asks for verification.
      document.write(response.data)
    } else {
      App.events.publish(
        'app:notification:flash:success',
        translate('devise.sessions.signed_in')
      )

      if (onSuccess) {
        onSuccess(response)
      } else {
        window.location.reload(true)
      }
    }
  }

  function onSubmitFailure(xhr) {
    const { email } = getValues()
    const status = xhr.response?.status
    const responseErrors = xhr.response?.data?.errors
    const ignoredStatuses = [200, 401, 429]

    if (!ignoredStatuses.includes(status)) {
      captureException(new Error('Login submission failed'), {
        tags: { section: 'React login' },
        extra: {
          msg: 'Login submission failure',
          email,
          xhrResponse: xhr.response?.data,
          status,
        },
      })
    }

    if (loginCaptchaEnabled) {
      window.mtcaptcha.resetUI(captchaId)
    }

    const errorMessage = status === 429 || !responseErrors
      ? translate('login.generic_auth_error')
      : responseErrors

    const message = Array.isArray(errorMessage) ? errorMessage[0] : errorMessage

    if (window.showNotification) {
      window.showNotification(message, { type: 'error', autohide: true })
    } else {
      App.events.publish('app:notification:flash:error', message)
    }
  }

  function onFormSubmit(data) {
    const { email } = data

    try {
      const captchaIsNotValid = !captchaIsValid()

      if (captchaIsNotValid) {
        return null
      }

      const form = new FormData()
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      form.append('authenticity_token', csrfToken)

      if (loginCaptchaEnabled) {
        form.append('MTCaptchaToken', window.mtcaptcha.getVerifiedToken(captchaId))
      }

      form.append('location', window.location.pathname)

      form.append('user[login]', data.email)
      form.append('user[password]', data.password)

      if (remember) {
        form.append('user[remember_me]', 1)
      }

      if (data.email_confirmation) {
        form.append('new_user[email_confirmation]', data.email_confirmation)
      }

      axios({
        url: '/users/2fa-or-login.json',
        data: form,
        method: 'post',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      })
        .then(response => onSubmitSuccess(response))
        .catch(onSubmitFailure)
    } catch (error) {
      captureException(error, {
        tags: { section: 'React login' },
        extra: { msg: 'Login error', email },
      })
    }
  }

  function onTwoFaSubmit(e) {
    e.preventDefault()

    if (twoFaCode === '') {
      App.events.publish('app:notification:flash:error', translate('login.two_fa.error_empty'))
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const { email, password } = getValues()

    axios
      .post('/users/2fa-or-login', {
        user: {
          login: email,
          password,
          remember_me: remember,
        },
        code: twoFaCode,
        location: window.location.pathname,
      }, {
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      })
      .then(response => onSubmitSuccess(response))
      .catch(onSubmitFailure)
  }

  function onTwoFaCodeChange(e) {
    setTwoFaCode(e.target.value)
  }

  function onRememberToggle() {
    setRemember(!remember)
  }

  function onShowPasswordToggle() {
    setShowPassword(!showPassword)
  }

  const token = document.querySelector('meta[name="csrf-token"]')?.content

  return {
    token,
    control,
    errors,
    touchedFields,
    showPassword,
    twoFASecondState,
    twoFaCode,
    loginCaptchaEnabled,
    remember,
    handleSubmit,
    onFormSubmit,
    onTwoFaSubmit,
    onTwoFaCodeChange,
    onRememberToggle,
    onShowPasswordToggle,
  }
}
